import { useState } from 'react';
import { Box, Group, ScrollArea, Stack, Textarea } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ModalV2 } from '@dealstack/frontend/shared/components/ModalV2/ModalV2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { InvestmentProgrammeDocumentRes } from '@dealstack/shared/lib/generated';

interface Props {
	documents: InvestmentProgrammeDocumentRes[];
	onClose: () => void;
	onConfirm: (reason: string) => void;
	isLoading?: boolean;
	allDocumentsSelected?: boolean;
	totalCount?: number;
}

export const RevokeDocumentModal = ({
	documents,
	onClose,
	onConfirm,
	isLoading,
	allDocumentsSelected,
	totalCount,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [reason, setReason] = useState('');

	return (
		<ModalV2 size="md" onClose={onClose} reactModalProps={{ shouldCloseOnOverlayClick: false }}>
			<Stack gap="md">
				<TextV2 variant="sans/17/600">
					{tMipManage('documents_page.revoke_simple_modal.title')}
				</TextV2>

				{allDocumentsSelected ? (
					<Box p="xs">
						<TextV2 variant="sans/14/600">
							{tMipManage(
								'documents_page.revoke_simple_modal.all_documents_selected',
								{ count: totalCount ?? 0 }
							)}
						</TextV2>
					</Box>
				) : (
					<ScrollArea.Autosize mah={300}>
						<Stack gap="xs" p="xs">
							{documents.map((doc, index) => (
								<Box
									key={doc.documentWorkflowInstanceId}
									p="xs"
									sx={(theme) => ({
										borderBottom:
											index < documents.length - 1
												? `1px solid ${theme.colors.gray[2]}`
												: undefined,
									})}
								>
									<Stack gap={4}>
										<TextV2 variant="sans/14/600">
											{doc.templateName ?? doc.fileName ?? 'Untitled'}
										</TextV2>
										{doc.investor.map((inv) => (
											<TextV2
												key={`${inv.firstName}-${inv.lastName}-${inv.status}`}
												variant="sans/11/400"
											>
												{[inv.firstName, inv.lastName]
													.filter(Boolean)
													.join(' ')}
												{doc.workflowStatus
													? ` - ${doc.workflowStatus}`
													: ''}
											</TextV2>
										))}
									</Stack>
								</Box>
							))}
						</Stack>
					</ScrollArea.Autosize>
				)}

				<Textarea
					label={tMipManage('documents_page.revoke_simple_modal.reason_label')}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder={tMipManage(
						'documents_page.revoke_simple_modal.reason_placeholder'
					)}
					maxLength={1000}
					minRows={4}
					autosize
					styles={{ label: { fontWeight: 600 } }}
				/>

				<Group justify="right" gap="xs">
					<ButtonV2 size="s" buttonTheme="light" variant="secondary" onClick={onClose}>
						{tMipManage('documents_page.revoke_simple_modal.cancel_button')}
					</ButtonV2>
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="deletion"
						disabled={!reason.trim() || isLoading}
						onClick={() => onConfirm(reason.trim())}
					>
						{tMipManage('documents_page.revoke_simple_modal.confirm_button')}
					</ButtonV2>
				</Group>
			</Stack>
		</ModalV2>
	);
};
